import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import type { AccessibilityProps, ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { slugify } from '../../utils';
import { ProductIngredientsList } from '../ProductIngredientsList';
import {
  LineItemDescription,
  LineItemFooter,
  LineItemHeader,
  LineItemImage,
  LineItemPressableHeader,
  LineItemPrice,
} from './subcomponents';

// ────────────────────────────────────────────────────────────────────────────────

export const ProductLineItemCard = (props: ProductLineItemCardProps) => {
  const {
    cloudinaryImageUrl = '',
    productName,
    description = '',
    price,
    addedIngredients = [],
    addedIngredientsLabel = 'Add:',
    removedIngredients = [],
    removedIngredientsLabel = 'Remove:',
    isDisabled,
    shareUrl = '',
    accessibilityLabel,
    onPress,
    onShare,
  } = props;

  const productSlug = slugify(productName);
  const shouldShowProductImage = Boolean(cloudinaryImageUrl);

  // ─────────────────────────────────────────────────────────────────

  const handleShare = useCallback(() => {
    onShare?.(shareUrl, productName);
  }, [onShare, shareUrl, productName]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <View testID={`sg-line-item-card-${productSlug}`} style={styles.card}>
      {shouldShowProductImage ? (
        <View style={styles.imgWrapper}>
          <LineItemImage
            cloudinaryImageUrl={cloudinaryImageUrl}
            onPress={onPress}
            isDisabled={isDisabled}
            accessibilityLabel={accessibilityLabel}
          />
        </View>
      ) : null}

      <ContentWrapper>
        <DetailsWrapper>
          <View style={styles.detailsColumn}>
            {onPress ? (
              <LineItemPressableHeader
                productName={productName}
                productSlug={productSlug}
                onPress={onPress}
                disabled={isDisabled}
                accessibilityLabel={accessibilityLabel}
                accessibilityState={{ disabled: isDisabled }}
              />
            ) : (
              <LineItemHeader
                productName={productName}
                productSlug={productSlug}
              />
            )}

            <ProductIngredientsList
              addedIngredientsLabel={addedIngredientsLabel}
              addedIngredients={addedIngredients}
              addedIngredientsListTestID={`sg-line-item-card-added-ingredients-${productSlug}`}
              removedIngredientsLabel={removedIngredientsLabel}
              removedIngredients={removedIngredients}
              removedIngredientsListTestID={`sg-line-item-card-removed-ingredients-${productSlug}`}
            />

            {description ? (
              <LineItemDescription
                selectable={!onPress}
                productSlug={productSlug}
              >
                {description}
              </LineItemDescription>
            ) : null}
          </View>

          {price ? <LineItemPrice price={price} /> : null}
        </DetailsWrapper>

        <LineItemFooter
          {...props}
          productSlug={productSlug}
          onShare={onShare ? handleShare : undefined}
        />
      </ContentWrapper>
    </View>
  );
};

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const ContentWrapper = (props: Pick<ViewProps, 'children'>) => {
  return <View style={styles.contentWrapper}>{props.children}</View>;
};

const DetailsWrapper = (props: Pick<ViewProps, 'children'>) => {
  return <View style={styles.detailsWrapper}>{props.children}</View>;
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  card: {
    paddingVertical: theme.spacing.large,
    flexDirection: 'row',
  },
  contentWrapper: {
    flex: 1,
    alignSelf: 'center',
  },
  imgWrapper: {
    paddingRight: theme.spacing.small,
  },
  detailsWrapper: {
    flexDirection: 'row',
  },
  detailsColumn: {
    flex: 1,
  },
});

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type ProductLineItemCardProps = {
  readonly productId: string;
  readonly productName: string;
  readonly description?: string;
  readonly cloudinaryImageUrl?: string;
  readonly price?: string;
  readonly quantity?: number;
  readonly removeBtnLabel?: string;
  readonly shareTooltipLabel?: string;
  readonly shareBtnLabel?: string;
  readonly shareUrl?: string;
  readonly isFetchingFavorite?: boolean;
  readonly isFavorite?: boolean;
  readonly isDisabled?: boolean;
  readonly onPress?: () => void;
  readonly onRemove?: (productId: string) => void;
  readonly onShare?: (productSlug: string, productName: string) => void;
  readonly onFavorite?: () => void;
  readonly onQuantityChange?:
    | ((selectedQuantity: number) => void)
    | ((selectedQuantity: number) => Promise<void>);
} & Pick<
  ComponentProps<typeof ProductIngredientsList>,
  | 'addedIngredientsLabel'
  | 'addedIngredients'
  | 'removedIngredientsLabel'
  | 'removedIngredients'
> &
  Pick<AccessibilityProps, 'accessibilityLabel'>;
